<i18n locale="en">
{
  "label": "Titles font",
  "sansSerif": "Sans serif",
  "serif": "Serif",
  "condensed": "Condensed",
  "custom": "Custom",
  "noFonts": "No fonts available"
}
</i18n>

<template>
  <!-- New UI when feature flag is enabled -->
  <OwnInputContainer v-if="isFontCustomizationEnabled" :label="t('label')">
    <div class="font-editor">
      <button
        v-for="option in presetOptions"
        :key="option.value"
        type="button"
        class="font-option"
        :class="{ 'font-option--selected': selectedFontType === option.value }"
        @click="onFontTypeSelect(option.value)"
      >
        <div class="font-option__radio">
          <div
            v-if="selectedFontType === option.value"
            class="font-option__radio-dot"
          />
        </div>
        <div class="font-option__label">
          {{ t(option.value) }}
        </div>
        <div
          class="font-option__preview"
          :style="{ fontFamily: option.previewFont }"
          aria-hidden="true"
          v-text="option.previewText"
        />
      </button>

      <!-- Custom font option with dropdown -->
      <div
        ref="customOption"
        class="font-option font-option--custom"
        :class="{ 'font-option--selected': selectedFontType === 'custom' }"
        @click="handleCustomClick"
      >
        <div class="font-option__radio">
          <div
            v-if="selectedFontType === 'custom'"
            class="font-option__radio-dot"
          />
        </div>
        <div class="font-option__label">
          {{ t('custom') }}
        </div>
        <input
          v-if="isCustomDropdownOpen"
          ref="customInput"
          v-model="customFontSearch"
          class="font-option__search-input"
          type="text"
          @focus="isCustomDropdownOpen = true"
          @click.stop
        />
        <div
          v-else-if="currentCustomFontLabel"
          class="font-option__custom-font-name"
          :style="{ fontFamily: customPreviewFont }"
          @click.stop="handleCustomClick"
        >
          {{ currentCustomFontLabel }}
        </div>
        <div
          v-else
          class="font-option__chevron-wrapper"
          @click.stop="handleCustomClick"
        >
          <IconChevronRight :size="16" class="font-option__chevron" />
        </div>

        <!-- Dropdown menu -->
        <div v-if="isCustomDropdownOpen" class="font-dropdown" @click.stop>
          <div
            v-if="filteredCustomFonts.length === 0"
            class="font-dropdown__empty"
          >
            {{ t('noFonts') }}
          </div>
          <template
            v-for="(font, index) in filteredCustomFonts"
            :key="font.value"
          >
            <div
              class="font-dropdown__item"
              :class="{
                'font-dropdown__item--selected':
                  font.label === currentCustomFontLabel,
              }"
              :style="{ fontFamily: font.previewFont }"
              @click="selectCustomFont(font)"
            >
              {{ font.label }}
            </div>
            <div
              v-if="font.label === currentCustomFontLabel && index === 0"
              class="font-dropdown__divider"
            />
          </template>
        </div>
      </div>
    </div>
  </OwnInputContainer>

  <!-- Old UI when feature flag is disabled -->
  <FontEditorOld v-else />
</template>

<script>
import set from 'lodash/set'
import { useI18n } from 'vue-i18n'
import { mapActions } from 'vuex'

import IconChevronRight from '@/icons/IconChevronRight.vue'
import { useCurrentTheme } from '@/lib/builder/public/preview/mixins/useCurrentTheme'
import { OwnInputContainer } from '@/ui/OwnInputContainer'
import { useReactiveFeatureFlag } from '@/utils/useFeatureFlags'

import FontEditorOld from './FontEditorOld.vue'

const PREVIEW_TEXT = 'Aa'

const fontMapping = {
  anton: 'Anton',
  bbhHegarty: 'BBH Hegarty',
  bebasNeue: 'Bebas Neue',
  boogaloo: 'Boogaloo',
  cabin: 'Cabin',
  cardo: 'Cardo',
  chango: 'Chango',
  chic: 'Abril Fatface',
  chicle: 'Chicle',
  cinzel: 'Cinzel',
  coiny: 'Coiny',
  concertOne: 'Concert One',
  condensed: 'Archivo Narrow',
  crushed: 'Crushed',
  dmSans: 'DM Sans',
  elegant: 'Playfair Display',
  geometric: 'Poppins',
  gfsNeohellenic: 'GFS Neohellenic',
  homemadeApple: 'Homemade Apple',
  ibmPlexSans: 'IBM Plex Sans',
  lemonada: 'Lemonada',
  lobster: 'Lobster',
  lobsterTwo: 'Lobster Two',
  lora: 'Lora',
  luxury: 'Noto Serif Display',
  macondo: 'Macondo',
  manrope: 'Manrope',
  merriweather: 'Merriweather',
  michroma: 'Michroma',
  modern: 'Inter',
  montserrat: 'Montserrat',
  nunito: 'Nunito',
  pacifico: 'Pacifico',
  permanentMarker: 'Permanent Marker',
  prata: 'Prata',
  raleway: 'Raleway',
  roboto: 'Roboto',
  sansSerif: 'Geist',
  serif: 'Source Serif 4',
  shantellSans: 'Shantell Sans',
  super: 'Syne',
  teko: 'Teko',
  unbounded: 'Unbounded',
  urban: 'Oswald',
  viga: 'Viga',
}

// Serif fonts that should fall back to generic 'serif'
const serifFonts = new Set([
  'chic',
  'elegant',
  'luxury',
  'serif',
  'cinzel',
  'prata',
])

// Legacy fonts that go in the custom dropdown
const legacyFonts = [
  { label: 'Abril Fatface', value: 'chic' },
  { label: 'Anton', value: 'anton' },
  { label: 'BBH Hegarty', value: 'bbhHegarty' },
  { label: 'Bebas Neue', value: 'bebasNeue' },
  { label: 'Boogaloo', value: 'boogaloo' },
  { label: 'Cabin', value: 'cabin' },
  { label: 'Cardo', value: 'cardo' },
  { label: 'Chango', value: 'chango' },
  { label: 'Chicle', value: 'chicle' },
  { label: 'Cinzel', value: 'cinzel' },
  { label: 'Coiny', value: 'coiny' },
  { label: 'Concert One', value: 'concertOne' },
  { label: 'Crushed', value: 'crushed' },
  { label: 'DM Sans', value: 'dmSans' },
  { label: 'GFS Neohellenic', value: 'gfsNeohellenic' },
  { label: 'Homemade Apple', value: 'homemadeApple' },
  { label: 'IBM Plex Sans', value: 'ibmPlexSans' },
  { label: 'Inter', value: 'modern' },
  { label: 'Lemonada', value: 'lemonada' },
  { label: 'Lobster', value: 'lobster' },
  { label: 'Lobster Two', value: 'lobsterTwo' },
  { label: 'Lora', value: 'lora' },
  { label: 'Macondo', value: 'macondo' },
  { label: 'Manrope', value: 'manrope' },
  { label: 'Merriweather', value: 'merriweather' },
  { label: 'Michroma', value: 'michroma' },
  { label: 'Montserrat', value: 'montserrat' },
  { label: 'Noto Serif Display', value: 'luxury' },
  { label: 'Nunito', value: 'nunito' },
  { label: 'Oswald', value: 'urban' },
  { label: 'Pacifico', value: 'pacifico' },
  { label: 'Permanent Marker', value: 'permanentMarker' },
  { label: 'Playfair Display', value: 'elegant' },
  { label: 'Poppins', value: 'geometric' },
  { label: 'Prata', value: 'prata' },
  { label: 'Raleway', value: 'raleway' },
  { label: 'Roboto', value: 'roboto' },
  { label: 'Shantell Sans', value: 'shantellSans' },
  { label: 'Syne', value: 'super' },
  { label: 'Teko', value: 'teko' },
  { label: 'Unbounded', value: 'unbounded' },
  { label: 'Viga', value: 'viga' },
]

const presetOptions = ['sansSerif', 'serif', 'condensed'].map((value) => {
  const fontFamily = fontMapping[value]
  const fallback = serifFonts.has(value) ? 'serif' : 'sans-serif'
  const previewFont = fontFamily.includes(' ')
    ? `"${fontFamily}", ${fallback}`
    : `${fontFamily}, ${fallback}`

  return {
    previewFont,
    previewText: PREVIEW_TEXT,
    value,
  }
})

export default {
  name: 'FontEditor',
  components: {
    FontEditorOld,
    IconChevronRight,
    OwnInputContainer,
  },
  mixins: [useCurrentTheme],
  data() {
    return {
      customFontSearch: '',
      isCustomDropdownOpen: false,
      presetOptions,
    }
  },
  setup() {
    const { t } = useI18n()
    const { enabled: isFontCustomizationEnabled } = useReactiveFeatureFlag(
      'hermes.fontCustomizationUI'
    )

    return {
      isFontCustomizationEnabled,
      t,
    }
  },
  computed: {
    currentCustomFontLabel() {
      const pairing = this.themeFont

      // Find the custom font label based on legacy pairing
      const fontEntry = legacyFonts.find((font) => font.value === pairing)

      return fontEntry ? fontEntry.label : null
    },
    customPreviewFont() {
      const fontName = this.currentCustomFontLabel
      if (fontName) {
        // Find the font value to check if it's serif
        const fontEntry = legacyFonts.find((f) => f.label === fontName)
        const fallback =
          fontEntry && serifFonts.has(fontEntry.value) ? 'serif' : 'sans-serif'
        return fontName.includes(' ')
          ? `"${fontName}", ${fallback}`
          : `${fontName}, ${fallback}`
      }
      return 'Geist, sans-serif'
    },
    filteredCustomFonts() {
      // Generate font list with preview fonts using proper fallbacks
      const customFonts = legacyFonts.map((font) => {
        const fallback = serifFonts.has(font.value) ? 'serif' : 'sans-serif'
        const previewFont = font.label.includes(' ')
          ? `"${font.label}", ${fallback}`
          : `${font.label}, ${fallback}`

        return {
          label: font.label,
          previewFont,
          value: font.value,
        }
      })

      // Filter by search if there's a search term
      let fonts = customFonts
      if (this.customFontSearch) {
        const search = this.customFontSearch.toLowerCase()
        fonts = customFonts.filter((font) =>
          font.label.toLowerCase().includes(search)
        )
      }

      // Sort so the selected font appears first
      const selectedFont = this.currentCustomFontLabel
      if (selectedFont) {
        return fonts.sort((a, b) => {
          if (a.label === selectedFont) return -1
          if (b.label === selectedFont) return 1
          return 0
        })
      }

      return fonts
    },
    selectedFontType() {
      const pairing = this.themeFont

      // Direct match for new pairings
      if (pairing === 'sansSerif') return 'sansSerif'
      if (pairing === 'serif') return 'serif'
      if (pairing === 'condensed') return 'condensed'

      // Check if it's a legacy font selection
      if (this.currentCustomFontLabel) {
        return 'custom'
      }

      return 'sansSerif'
    },
    themeFont() {
      const { currentTheme } = this

      return currentTheme?.overrides?.font?.pairing ?? 'modern'
    },
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside)
    document.addEventListener('keydown', this.handleEscapeKey)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside)
    document.removeEventListener('keydown', this.handleEscapeKey)
  },
  methods: {
    ...mapActions({
      updateResource: 'websiteBuilder/updateResource',
    }),
    closeCustomDropdown() {
      this.isCustomDropdownOpen = false
      this.customFontSearch = ''
    },
    handleClickOutside(event) {
      if (
        this.isCustomDropdownOpen &&
        this.$refs.customOption &&
        !this.$refs.customOption.contains(event.target)
      ) {
        this.closeCustomDropdown()
      }
    },
    handleCustomClick() {
      this.openCustomDropdown()
    },
    handleEscapeKey(event) {
      if (event.key === 'Escape' && this.isCustomDropdownOpen) {
        this.closeCustomDropdown()
      }
    },
    async onFontTypeSelect(fontType) {
      this.closeCustomDropdown()

      if (this.selectedFontType === fontType) return

      const update = set({}, 'theme.overrides.font.pairing', fontType)

      await this.updateResource(update)
    },
    openCustomDropdown() {
      if (this.isCustomDropdownOpen) return

      this.isCustomDropdownOpen = true
      this.customFontSearch = ''

      this.$nextTick(() => {
        this.$refs.customInput?.focus()
      })
    },
    async selectCustomFont(font) {
      this.closeCustomDropdown()

      // Use the legacy pairing value
      const update = set({}, 'theme.overrides.font.pairing', font.value)

      await this.updateResource(update)
    },
  },
}
</script>

<style lang="scss" scoped>
.font-editor {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-bottom: 300px;
}

.font-option {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 8px 12px;
  background: var(--background-primary);
  border: 1px solid transparent;
  border-radius: var(--radius-regular);
  box-shadow: var(--shadow-card-default);
  cursor: pointer;
  transition:
    background-color 0.2s ease,
    opacity 0.2s ease;

  &:hover {
    background: var(--background-active);
    opacity: 1;
  }

  &--selected {
    background: var(--background-active);
  }

  &--custom {
    position: relative;
    cursor: pointer;
    user-select: none;
  }

  &__radio {
    width: 16px;
    height: 16px;
    border: 2px solid var(--divider);
    border-radius: 50%;
    margin-right: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &--selected &__radio {
    border-color: var(--misc-brand);
  }

  &__radio-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--misc-brand);
  }

  &__label {
    flex: 1;
    text-align: left;
    font-size: 16px;
    font-weight: 500;
    color: var(--text-color-primary);
    cursor: pointer;
  }

  &__search-input {
    margin-left: auto;
    width: 160px;
    font-size: 16px;
    font-weight: 500;
    color: var(--text-color-primary);
    background: transparent;
    border: none;
    outline: none;
    padding: 0;
    text-align: right;
  }

  &__custom-font-name {
    margin-left: auto;
    font-size: 16px;
    font-weight: 500;
    color: var(--text-color-secondary);
    cursor: pointer;
  }

  &__chevron-wrapper {
    margin-left: auto;
    display: flex;
    align-items: center;
    cursor: pointer;
  }

  &__chevron {
    color: var(--text-color-secondary);
    transform: rotate(90deg);
    flex-shrink: 0;
  }

  &__preview {
    font-size: 20px;
    font-weight: 600;
    color: var(--text-color-secondary);
    margin-left: auto;
    cursor: pointer;
  }
}

.font-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  background: var(--background-primary);
  border: 1px solid var(--divider);
  border-radius: var(--radius-regular);
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  max-height: 240px;
  overflow-y: auto;
  z-index: 100;
  padding: 4px;

  /* Hide scrollbar */
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */

  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }

  &__empty {
    padding: 12px 16px;
    color: var(--text-color-tertiary);
    font-size: 14px;
    text-align: center;
  }

  &__item {
    padding: 10px 8px;
    cursor: pointer;
    transition: background-color 0.2s ease;
    font-size: 16px;
    font-weight: 500;
    color: var(--text-color-primary);
    border-radius: var(--radius-regular);
    margin-bottom: 2px;

    &:hover {
      background: var(--background-active);
    }

    &--selected {
      background: var(--background-active);
      margin-bottom: 2px;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__divider {
    height: 1px;
    background: var(--divider);
    margin: 6px 8px;
  }
}
</style>
